import { colord } from 'colord';
import { getSymbolSvgUrl, type UscgSymbol } from './utils/uscgSymbology';

const DEFAULT_COLOR = '#1e40af';

type CustomSwatch = {
  kind: 'point' | 'line' | 'polygon';
  color: string;
  opacity: number;
};

function readCustomSwatch(
  resourceType: UscgSymbol,
  properties: unknown
): CustomSwatch | null {
  const props =
    properties && typeof properties === 'object'
      ? (properties as Record<string, unknown>)
      : {};
  if (resourceType === 'customPoint') {
    return {
      kind: 'point',
      color:
        typeof props.pointColor === 'string' ? props.pointColor : DEFAULT_COLOR,
      opacity: typeof props.pointOpacity === 'number' ? props.pointOpacity : 1,
    };
  }
  if (resourceType === 'customLine') {
    return {
      kind: 'line',
      color: '#1d4ed8',
      opacity: 1,
    };
  }
  if (resourceType === 'customPolygon') {
    return {
      kind: 'polygon',
      color:
        typeof props.polygonColor === 'string'
          ? props.polygonColor
          : DEFAULT_COLOR,
      opacity:
        typeof props.polygonOpacity === 'number' ? props.polygonOpacity : 0.25,
    };
  }
  return null;
}

function paint(color: string, opacity: number) {
  const parsed = colord(color);
  const { r, g, b } = parsed.isValid()
    ? parsed.toRgb()
    : { r: 30, g: 64, b: 175 };
  const alpha = Math.max(0, Math.min(1, opacity));
  return {
    fill: `rgba(${r}, ${g}, ${b}, ${alpha})`,
    stroke: `rgb(${r}, ${g}, ${b})`,
  };
}

export function InternalObjectSymbol({
  resourceType,
  properties,
}: {
  resourceType: UscgSymbol;
  properties: unknown;
}) {
  const swatch = readCustomSwatch(resourceType, properties);

  if (!swatch) {
    return (
      <img
        src={getSymbolSvgUrl(resourceType)}
        alt=""
        width={18}
        height={18}
        className="tw:shrink-0"
      />
    );
  }

  const { fill, stroke } = paint(swatch.color, swatch.opacity);

  if (swatch.kind === 'line') {
    return (
      <svg
        width={18}
        height={18}
        viewBox="0 0 18 18"
        aria-hidden
        className="tw:shrink-0"
      >
        <line
          x1="2"
          y1="14"
          x2="16"
          y2="4"
          stroke={stroke}
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
    );
  }

  if (swatch.kind === 'point') {
    return (
      <svg
        width={18}
        height={18}
        viewBox="0 0 18 18"
        aria-hidden
        className="tw:shrink-0"
      >
        <circle
          cx="9"
          cy="9"
          r="5.5"
          fill={fill}
          stroke={stroke}
          strokeWidth="1.5"
        />
      </svg>
    );
  }

  return (
    <svg
      width={18}
      height={18}
      viewBox="0 0 32 32"
      aria-hidden
      className="tw:shrink-0"
    >
      <polygon
        points="16,3 28,10 24,26 8,29 3,13"
        fill={fill}
        stroke={stroke}
        strokeWidth="2"
      />
    </svg>
  );
}
